import type { CarSpec, Gear } from './types';

/**
 * Reparto de frenada, ABS con pulso y retención en pendiente (hill hold).
 * Módulo puro y determinista; el pulso del ABS se deriva del tick de simulación.
 */

export interface BrakeState {
  /** Tiempo restante de retención en pendiente (s). */
  holdTimerS: number;
  brakeWasPressed: boolean;
}

export function createBrakes(): BrakeState {
  return { holdTimerS: 0, brakeWasPressed: false };
}

export interface BrakeInput {
  brake: number; // 0..1
  throttle: number;
  handbrake: boolean;
  gear: Gear;
  speedMs: number; // con signo
  /** Límite de agarre por eje (N): [delantero, trasero]. */
  gripCapAxleN: [number, number];
  /** Pendiente (rad, + = cuesta arriba en el sentido de la marcha engranada). */
  slopeRad: number;
  massKg: number;
  tick: number;
  dt: number;
  absAvailable: boolean;
  hillHoldAvailable: boolean;
}

export interface BrakeOutput {
  frontAxleN: number;
  rearAxleN: number;
  absActive: boolean;
  hillHold: boolean;
  parkLock: boolean;
}

const FRONT_BIAS = 0.63;
export const PARK_LOCK_N = 30000;
export const HANDBRAKE_N = 5200;

export function stepBrakes(st: BrakeState, spec: CarSpec, inp: BrakeInput): BrakeOutput {
  const absSpeed = Math.abs(inp.speedMs);
  const demand = inp.brake * spec.brakeForceN;
  let front = demand * FRONT_BIAS;
  let rear = demand * (1 - FRONT_BIAS);
  let absActive = false;

  // ABS: si la demanda supera el agarre, modula con pulso (~10 Hz a 60 fps)
  const modulate = (axleDemand: number, cap: number): number => {
    if (axleDemand <= cap) return axleDemand;
    if (inp.absAvailable && absSpeed > 1.7) {
      absActive = true;
      const pulse = Math.floor(inp.tick / 3) % 2 === 0;
      return cap * (pulse ? 0.98 : 0.62);
    }
    return Math.min(axleDemand, cap * 1.02);
  };
  front = modulate(front, inp.gripCapAxleN[0]);
  rear = modulate(rear, inp.gripCapAxleN[1]);

  if (inp.handbrake) rear += HANDBRAKE_N;

  // Hill hold: al soltar el freno en pendiente, retiene ~2,5 s hasta acelerar
  let hillHold = false;
  if (
    inp.hillHoldAvailable &&
    (inp.gear === 'D' || inp.gear === 'R') &&
    absSpeed < 0.45 &&
    inp.slopeRad > 0.03 // solo si la pendiente empuja hacia atrás
  ) {
    if (inp.brake > 0.15) {
      st.holdTimerS = 2.5;
    } else if (st.holdTimerS > 0 && inp.throttle < 0.12) {
      st.holdTimerS = Math.max(0, st.holdTimerS - inp.dt);
      const holdForce = Math.min(
        inp.gripCapAxleN[0] + inp.gripCapAxleN[1],
        inp.massKg * 9.81 * Math.sin(inp.slopeRad) * 1.4 + 300,
      );
      front += holdForce * FRONT_BIAS;
      rear += holdForce * (1 - FRONT_BIAS);
      hillHold = true;
    }
  } else if (inp.brake > 0.15) {
    st.holdTimerS = 0;
  }
  if (inp.throttle > 0.12) st.holdTimerS = 0;

  // P: pawl de aparcamiento (bloqueo mecánico) cuando el coche está casi parado
  let parkLock = false;
  if (inp.gear === 'P' && absSpeed < 3) {
    front += PARK_LOCK_N * FRONT_BIAS;
    rear += PARK_LOCK_N * (1 - FRONT_BIAS);
    parkLock = true;
  }

  st.brakeWasPressed = inp.brake > 0.15;
  return { frontAxleN: front, rearAxleN: rear, absActive, hillHold, parkLock };
}
